import { useState, useEffect, useRef, useCallback } from 'react'
import { Send, Loader2, AlertCircle, MessageSquare, RefreshCw } from 'lucide-react'
import { apiURL } from '../../../../utilities/URLs'
import { account } from '../../../../classes/Account'
import { refreshAccessToken } from '../../../../utilities/extensions'
import { useNavigate } from 'react-router-dom'
import usePortalSocket from '../../../../hooks/usePortalSocket'

import {
  formatMessageDateDividerLabel,
  formatMessageDateTimeTitle,
  formatMessageTimeShort,
  shouldShowMessageDateDivider
} from '../../../../utilities/messageDateLabels'

const PAGE_SIZE = 20

export default function ClientSMSChat({ applicationId }) {
  const navigate = useNavigate()
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const messagesEndRef = useRef(null)
  const scrollContainerRef = useRef(null)
  const inputRef = useRef(null)
  const isInitialLoad = useRef(true)

  // Real-time socket — appends agent messages as they arrive
  usePortalSocket({
    onInboundSMS: (data) => {
      const msg = {
        id: `rt-${Date.now()}`,
        body: data.messagePreview || '',
        direction: 'outbound',
        status: '',
        dateAdded: new Date().toISOString()
      }
      setMessages(prev => [...prev, msg])
      requestAnimationFrame(() => scrollToBottom())
    }
  })

  useEffect(() => {
    if (applicationId) {
      isInitialLoad.current = true
      fetchHistory()
    }
  }, [applicationId])

  useEffect(() => {
    if (isInitialLoad.current && messages.length > 0) {
      requestAnimationFrame(() => {
        const container = scrollContainerRef.current
        if (container) container.scrollTop = container.scrollHeight
        isInitialLoad.current = false
      })
    }
  }, [messages])

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior })
  }

  const fetchHistory = async (lastMessageId = null) => {
    try {
      if (lastMessageId) setIsLoadingMore(true)
      else setIsLoading(true)
      setError(null)

      const params = new URLSearchParams({ limit: PAGE_SIZE })
      if (lastMessageId) params.set('lastMessageId', lastMessageId)

      const res = await fetch(`${apiURL}/api/applications/${applicationId}/sms?${params}`, {
        headers: { 'Authorization': account.accessToken }
      })
      const data = await res.json()

      if (data.response?.error && !data.response?.messages?.length) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          if (refreshedToken && refreshedToken.length > 0) return await fetchHistory(lastMessageId)
          else return navigate('/', { state: { expired: true } })
        }
        setError(data.response.error)
        return
      }

      const newMessages = data.response?.messages || []
      setHasMore(data.response?.hasMore || false)

      const sortAsc = arr => [...arr].sort((a, b) => new Date(a.dateAdded) - new Date(b.dateAdded))

      if (lastMessageId) {
        const container = scrollContainerRef.current
        const previousHeight = container?.scrollHeight || 0
        setMessages(prev => sortAsc([...newMessages, ...prev]))
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (container) container.scrollTop = container.scrollHeight - previousHeight
          })
        })
      } else {
        setMessages(sortAsc(newMessages))
      }
    } catch (err) {
      setError('Failed to load messages.')
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }

  const handleLoadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || messages.length === 0) return
    const oldestMessageId = messages[0]?.id
    if (oldestMessageId) fetchHistory(oldestMessageId)
  }, [isLoadingMore, hasMore, messages])

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return
    if (container.scrollTop < 60 && hasMore && !isLoadingMore) handleLoadMore()
  }, [handleLoadMore, hasMore, isLoadingMore])

  const handleSend = async () => {
    if (!newMessage.trim() || isSending) return

    const messageText = newMessage.trim()
    setNewMessage('')

    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      body: messageText,
      direction: 'inbound',
      status: 'sending',
      dateAdded: new Date().toISOString()
    }
    setMessages(prev => [...prev, optimisticMsg])
    requestAnimationFrame(() => scrollToBottom())

    try {
      setIsSending(true)
      const res = await fetch(`${apiURL}/api/applications/${applicationId}/sms`, {
        method: 'POST',
        headers: {
          'Authorization': account.accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: messageText })
      })
      const data = await res.json()
      if (data.response?.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          if (refreshedToken && refreshedToken.length > 0) {
            const retryRes = await fetch(`${apiURL}/api/applications/${applicationId}/sms`, {
              method: 'POST',
              headers: {
                'Authorization': account.accessToken,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ message: messageText })
            })
            const retryData = await retryRes.json()
            if (retryData.response?.error) {
              setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id))
              setError(retryData.response.error)
            } else {
              setMessages(prev => prev.map(m =>
                m.id === optimisticMsg.id ? { ...m, id: retryData.response.ghlMessageId || m.id, status: 'sent' } : m
              ))
            }
          } else {
            navigate('/', { state: { expired: true } })
          }
        } else {
          setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id))
          setError(data.response.error)
        }
      } else {
        setMessages(prev => prev.map(m =>
          m.id === optimisticMsg.id ? { ...m, id: data.response.ghlMessageId || m.id, status: 'sent' } : m
        ))
      }
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id))
      setError('Failed to send message.')
    } finally {
      setIsSending(false)
      inputRef.current?.focus()
    }
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className='flex flex-col h-full relative overflow-hidden'>
      {/* Header */}
      <div className='flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700'>
        <div className='flex items-center gap-2'>
          <div className='w-8 h-8 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-emerald-900/40 dark:to-emerald-800/20 flex items-center justify-center shrink-0'>
            <MessageSquare size={14} className='text-emerald-600 dark:text-emerald-400' />
          </div>
          <h3 className='text-sm font-bold text-gray-900 dark:text-slate-100'>Agent Support Chat</h3>
        </div>
        <button
          onClick={() => { isInitialLoad.current = true; fetchHistory() }}
          className='p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-400 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer'
          title='Refresh'
          aria-label='Refresh messages'
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className='flex-1 min-w-0 overflow-y-auto overflow-x-hidden bg-gray-50/50 dark:bg-slate-900/50'
      >
        {isLoading ? (
          <div className='flex flex-col items-center justify-center h-full gap-2 opacity-60'>
            <Loader2 size={20} className='text-emerald-500 animate-spin' />
            <span className='text-[10px] font-medium text-gray-400 uppercase tracking-widest'>Loading</span>
          </div>
        ) : messages.length === 0 ? (
          <div className='flex flex-col items-center justify-center h-full px-6 text-center gap-3'>
            <div className='w-12 h-12 rounded-xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center'>
              <MessageSquare size={22} className='text-gray-400 dark:text-slate-500' />
            </div>
            <div>
              <h3 className='text-sm font-bold text-gray-900 dark:text-slate-100'>No messages yet</h3>
              <p className='text-xs text-gray-500 dark:text-slate-400 mt-1'>Send a message to your support agent below.</p>
            </div>
          </div>
        ) : (
          <div className='min-h-full flex flex-col justify-end px-3 py-3 space-y-4'>
            {isLoadingMore && (
              <div className='flex justify-center py-1'>
                <Loader2 size={14} className='text-emerald-500 animate-spin' />
              </div>
            )}
            {hasMore && !isLoadingMore && (
              <div className='flex justify-center'>
                <button
                  onClick={handleLoadMore}
                  className='text-[10px] font-bold text-blue-500 uppercase tracking-widest hover:underline cursor-pointer'
                >
                  Load Older Messages
                </button>
              </div>
            )}
            {messages.map((msg, i) => {
              // In GHL: 'inbound' = client→business (client's message), 'outbound' = business→client (agent's message)
              // In the client UI: client messages go on the right, agent messages on the left
              const isMine = msg.direction === 'inbound'
              const showDate = shouldShowMessageDateDivider(messages, i, (m) => m.dateAdded)

              const bubbleClass = isMine
                ? 'bg-gradient-to-br from-ertc-secondary to-ertc-secondary/90 text-white rounded-2xl rounded-tr-sm shadow-sm'
                : 'bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-100 border border-gray-200/60 dark:border-slate-600 rounded-2xl rounded-tl-sm shadow-sm'

              return (
                <div key={msg.id} className='group'>
                  {showDate && (
                    <div className='relative flex items-center justify-center mb-4'>
                      <div className='absolute inset-x-0 top-1/2 h-px bg-gray-200/60 dark:bg-slate-700' />
                      <span className='relative z-10 bg-gray-50 dark:bg-slate-800 px-2 text-[9px] font-bold text-gray-400 uppercase tracking-wider'>
                        {formatMessageDateDividerLabel(msg.dateAdded)}
                      </span>
                    </div>
                  )}
                  <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} items-end gap-1.5 mb-0.5`}>
                    {!isMine && (
                      <div className='w-5 h-5 rounded-full bg-gray-200 dark:bg-slate-600 flex items-center justify-center shrink-0 text-[8px] font-bold text-gray-500 dark:text-slate-300 mb-1'>
                        A
                      </div>
                    )}
                    <div className='min-w-0 max-w-[80%] space-y-0.5'>
                      <div className={`min-w-0 max-w-full px-3 py-2 text-[13px] leading-relaxed [overflow-wrap:anywhere] ${bubbleClass}`}>
                        {msg.body}
                      </div>
                      <div className={`flex items-center gap-1 px-0.5 ${isMine ? 'justify-end' : 'justify-start'}`}>
                        <span
                          className='text-[9px] font-medium text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity'
                          title={formatMessageDateTimeTitle(msg.dateAdded)}
                        >
                          {formatMessageTimeShort(msg.dateAdded)}
                        </span>
                        {isMine && (
                          <>
                            {msg.status === 'sending' && <Loader2 size={9} className='text-emerald-500 animate-spin' />}
                            {msg.status === 'sent' && <span className='text-[9px] text-emerald-500 font-bold'>Sent</span>}
                            {msg.status === 'delivered' && <span className='text-[9px] text-emerald-600 font-bold'>Delivered</span>}
                            {msg.status === 'failed' && <span className='text-[9px] text-red-500 font-bold'>Failed</span>}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
            <div ref={messagesEndRef} className='h-px' />
          </div>
        )}
      </div>

      {/* Error Toast */}
      {error && (
        <div className='absolute bottom-16 left-2 right-2 z-30'>
          <div className='flex items-center justify-between px-3 py-2 bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-900/50 rounded-lg shadow-lg'>
            <div className='flex items-center gap-2'>
              <AlertCircle size={14} className='text-red-500 dark:text-red-400' />
              <span className='text-[11px] font-medium text-red-700 dark:text-red-300'>{error}</span>
            </div>
            <button onClick={() => setError(null)} className='p-0.5 hover:bg-red-100 dark:hover:bg-red-900/50 rounded text-red-400 cursor-pointer'>✕</button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className='p-2 bg-white dark:bg-slate-800 border-t border-gray-100 dark:border-slate-700'>
        <div className='flex items-end gap-1.5 p-1 bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700 rounded-2xl transition-all focus-within:border-ertc-secondary/30 dark:focus-within:border-ertc-secondary/50'>
          <textarea
            ref={inputRef}
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='Type a message...'
            rows={1}
            maxLength={480}
            className='flex-1 pl-3 py-2 rounded-xl border-none outline-none text-sm resize-none leading-relaxed bg-transparent max-h-24 placeholder:text-gray-400 dark:text-slate-100'
            style={{ minHeight: '36px' }}
          />
          <button
            onClick={handleSend}
            disabled={!newMessage.trim() || isSending}
            className={`mb-0.5 mr-0.5 p-2 rounded-full transition-all shrink-0 flex items-center justify-center cursor-pointer
              ${!newMessage.trim() || isSending
                ? 'bg-gray-100 dark:bg-slate-700/50 text-gray-300 dark:text-slate-500 cursor-not-allowed'
                : 'bg-ertc-secondary text-white shadow-md hover:opacity-90 active:scale-95'
              }`}
            style={{ width: '32px', height: '32px' }}
            aria-label='Send message'
          >
            {isSending ? <Loader2 size={14} className='animate-spin' /> : <Send size={14} className='ml-0.5' />}
          </button>
        </div>
      </div>
    </div>
  )
}
